import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { detailsVacApp, updateVacApp } from "../../actions/vaccineAppointmentActions";
import { useNavigate, useParams } from "react-router-dom";

const styles = {
  form: {
    maxWidth: "1000px",
    margin: "30px auto",
    padding: "30px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    color: "#333",
  },
  formHeader: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#222",
    marginBottom: "10px",
    textAlign: "center",
  },
  formDescription: {
    fontSize: "15px",
    lineHeight: "1.6",
    color: "#555",
    textAlign: "center",
    marginBottom: "20px",
  },
  legend: {
    display: "flex",
    justifyContent: "space-around",
    background: "#f9fafb",
    padding: "12px",
    borderRadius: "8px",
    fontSize: "14px",
    marginBottom: "25px",
    border: "1px solid #eee",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: "25px 0 15px",
    color: "#444",
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: "5px",
  },
  fieldsContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
    marginBottom: "20px",
  },
  fieldWrapper: {
    background: "#fafafa",
    padding: "14px 16px",
    borderRadius: "10px",
    border: "1px solid #e6e6e6",
    transition: "0.2s",
  },
  label: {
    display: "block",
    marginBottom: "10px",
    fontWeight: "500",
    fontSize: "15px",
    color: "#333",
  },
  radioGroup: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
  },
  radioOption: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    color: "#444",
  },
  radioInput: {
    accentColor: "#2563eb", // blue radio
    transform: "scale(1.2)",
  },
  table: {
    width: "100%",
    marginTop: "30px",
    borderCollapse: "collapse",
    border: "1px solid #e5e7eb",
  },
  th: {
    padding: "12px",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    fontSize: "14px",
    textAlign: "center",
  },
  td: {
    padding: "12px",
    border: "1px solid #e5e7eb",
    fontSize: "14px",
    textAlign: "center",
  },
  submitButton: {
    marginTop: "25px",
    width: "100%",
    padding: "14px",
    background: "linear-gradient(90deg, #2563eb, #1d4ed8)",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "600",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "0.2s",
  },
};


const ISAA = () => {
    const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  // Redux state
   const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;
  const vaccineAppList = useSelector((state) => state.vaccineAppDetails);
  const { appointment: { appointment } = {} } = vaccineAppList || {};

  
  // ✅ prefill from existing appointment.isaa.responses (if available)
  const [responses, setResponses] = useState({});
  useEffect(() => {         
    if (appointment?.isaa?.responses) {
      setResponses(appointment.isaa.responses);
    }
  }, [appointment]);

  const handleChange = (questionText, score) => {
    setResponses((prev) => ({ ...prev, [questionText]: Number(score) }));
  };

  const calculateTotal = () =>
    Object.values(responses).reduce((acc, curr) => acc + curr, 0);

  const classify = () => {
    const score = calculateTotal();
    if (score < 70) return "No Autism";
    if (score <= 106) return "Mild Autism";
    if (score <= 153) return "Moderate Autism";
    return "Severe Autism";
  };

  const handleSubmit = () => {
    const totalScore = calculateTotal();
    const classificationResult = classify();

    const formResult = {
      responses,
      score: totalScore,
      classification: classificationResult,
    };

    console.log("Submitted JSON:", formResult);

    // Save score to localStorage for Psychodiagnostic form
    const localStorageKey = `psycho_tools_${id}`;
    const existingScores = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    existingScores['ISAA'] = totalScore;
    localStorage.setItem(localStorageKey, JSON.stringify(existingScores));

    if (appointment?._id) {
      dispatch(
        updateVacApp({
          _id: appointment._id,
          isaa: formResult, // ✅ saves as appointment.isaa
        })
      );
    }

    alert(
      `Form submitted!\nScore: ${totalScore}\nClassification: ${classificationResult}`
    );
  };

  useEffect(() => {
      if (userInfo) {
        dispatch(detailsVacApp(id));
      } else {
        navigate("/signin");
      }
    }, [dispatch, userInfo, id, navigate]);


  const sections = [
    {
      title: "I. SOCIAL RELATIONSHIP AND RECIPROCITY",
      items: [
        "Has poor eye contact",
        "Lacks social smile",
        "Remains aloof",
        "Does not reach out to others",
        "Unable to relate to people",
        "Unable to respond to social/environmental cues",
        "Engages in solitary and repetitive play activities",
        "Unable to take turns in social interaction",
        "Does not maintain peer relationships",
      ],
    },
    {
      title: "II. EMOTIONAL RESPONSIVENESS",
      items: [
        "Shows inappropriate emotional response",
        "Shows exaggerated emotions",
        "Engages in self-stimulating emotions",
        "Lacks fear of danger",
        "Excited or agitated for no apparent reason",
      ],
    },
    {
      title: "III. SPEECH-LANGUAGE AND COMMUNICATION",
      items: [
        "Acquired speech and lost it",
        "Has difficulty in using non-verbal language or gestures to communicate",
        "Engages in stereotyped and repetitive use of language",
        "Engages in echolalic speech",
        "Produces infantile speech/ unusual noises",
        "Unable to initiate or sustain conversation with others",
        "Uses jargon or meaningless words",
        "Uses pronoun reversals",
        "Unable to grasp pragmatics of communication (real meaning)",
      ],
    },
    {
      title: "IV. BEHAVIOUR PATTERNS",
      items: [
        "Engages in stereotyped and repetitive motor mannerisms",
        "Shows attachment to inanimate objects",
        "Shows hyperactivity/restlessness",
        "Exhibits aggressive behavior",
        "Throws temper tantrums",
        "Engages in self-injurious behavior",
        "Insists on sameness",
      ],
    },
    {
      title: "V. SENSORY ASPECTS",
      items: [
        "Unusually sensitive to sensory stimuli",
        "Stares into space for long periods of time",
        "Has difficulty in tracking objects",
        "Has unusual vision",
        "Insensitive to pain",
        "Responds to objects/people unusually by smelling, touching or tasting",
      ],
    },
    {
      title: "VI. COGNITIVE COMPONENT",
      items: [
        "Inconsistent attention and concentration",
        "Shows delay in responding",
        "Has unusual memory of some kind",
        "Has ‘savant’ ability",
      ],
    },
  ];

  return (
    <div style={styles.form}>
      <h2 style={styles.formHeader}>Indian Scale for Assessment of Autism (ISAA)</h2>
      <p style={styles.formDescription}>
        Below are given 40 statements which are divided under six domains, please tick (o) mark the appropriate rating for each item of the scale by observing the child and by interviewing the parents in order to assess Autism
      </p>
      <div style={styles.legend}>
        <span>1 = Rarely (≤20%)</span>
        <span>2 = Sometimes (21–40%)</span>
        <span>3 = Frequently (41–60%)</span>
        <span>4 = Mostly (61–80%)</span>
        <span>5 = Always (81–100%)</span>
      </div>

      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex}>
          <h3 style={{ color: "#444", margin: "20px 0 10px" }}>{section.title}</h3>
          <div style={styles.fieldsContainer}>
            {section.items.map((item, itemIndex) => (
              <div key={itemIndex} style={styles.fieldWrapper}>
                <label style={styles.label}>{item}</label>
                <div style={styles.radioGroup}>
                  {[1, 2, 3, 4, 5].map((score) => (
                    <label key={score} style={styles.radioOption}>
                      <input
                        type="radio"
                        name={item}
                        value={score}
                        style={styles.radioInput}
                        checked={responses[item] === score}
                        onChange={() => handleChange(item, score)}
                      />
                      {score}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Classification</th>
            <th style={styles.th}>No Autism <br /> &lt; 70</th>
            <th style={styles.th}>Mild Autism <br /> 70 to 106</th>
            <th style={styles.th}>Moderate Autism <br /> 107 to 153</th>
            <th style={styles.th}>Severe Autism <br /> &gt; 153</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...styles.td, fontWeight: "bold" }}>Total score</td>
            <td style={styles.td} colSpan={4}>
              <strong>
                {calculateTotal()} ({classify()})
              </strong>
            </td>
          </tr>
        </tbody>
      </table>

      <button onClick={handleSubmit} style={styles.submitButton}>
        Submit
      </button>
    </div>
  );
};

export default ISAA;
